import Cookie from "js-cookie";
import { replace } from "infrastructure/router";
import type { ResourceResponse } from "types";
import { WebRoutes } from "consts/enpoints/web";
import { AUTH_DATA_KEY } from "consts/general";
import { ResponseCode } from "consts/api";
import { RoleName, TRUST_TOKEN_COOKIE_KEY } from "consts/auth";
import Storage from "util/storage";
import type { AuthData, AuthResponse, AuthUserPayload } from "features/auth/types";

export const getAuthData = () =>
    Storage.getItem<AuthData>(AUTH_DATA_KEY);

export const isAuthDataAvailable = () =>
    Boolean(getAuthData());

export const getUserPayload = () => {
    const authData = getAuthData();

    // Sessions stored before the cookie switch have no user fields
    if (!authData?.userId) {
        return;
    }

    return authData as AuthData & AuthUserPayload;
};

export const getUserId = () => getUserPayload()?.userId;

export const getRoleName = () =>
    getAuthData()?.role_name;

// The JWT lives only in the HttpOnly cookie set by the API
export const setAuthData = ({
    access_token: _,
    ...authData
}: AuthResponse['data'] & Pick<AuthData, 'timestamp'>) => {
    Storage.setItem(AUTH_DATA_KEY, authData);
};

// The API keeps the trust token in an HttpOnly cookie now
export const removeLegacyTrustToken = () => {
    Cookie.remove(TRUST_TOKEN_COOKIE_KEY);
};

export const revokeAuthData = () => {
    Storage.removeItem(AUTH_DATA_KEY);
};

export const isCodeIncludes = (code: number) =>
    [ResponseCode.Unathenticated].includes(code);

export const isResponseValid = (response: ResourceResponse<unknown>) =>
    !isCodeIncludes(response?.code);

export const isEmployee = (roleName: string) =>
    Object.is(roleName, RoleName.MerchantEmployee);

export const isMerchant = (_: string) => true;

export const isAdmin = (roleName: string) => [
    RoleName.MerchantAdmin,
].includes(roleName as RoleName);

export const withLoader = (api: (params: AuthUserPayload & AuthData) => Promise<Response>) =>
    async () => {
        const userPayload = getUserPayload();

        if (!userPayload) {
            return replace(WebRoutes.Login.Index);
        }

        const response = await api(userPayload);

        return response.json();
    };
